import { Result } from '@blade/std-lib'

import { DomUtil } from '..'
import { environment } from '../../environment'
import { copyToClipboard } from '../DomUtil'
import { UnknownError } from '../Error'

import { PlatformError } from './platform-error'
import { getFallbackDeviceIdFromLocalStorage } from './shared-platform-util'

import type { ImpactHapticFeedbackStyle } from '..'
import type { DeviceId } from './device-id'
import type * as PlatformUtil from './platform-util'

const setScaleFactor = async (scaleFactor: number) => {
  window.document.body.style.zoom = scaleFactor.toString()

  return Result.succeed(scaleFactor)
}

let cachedDeviceId: DeviceId | undefined
const impactDuration: Record<ImpactHapticFeedbackStyle, Array<number>> = {
  light: [20],
  medium: [40],
  heavy: [60],

  soft: [10, 15, 10],
  rigid: [43],
}
export const BrowserPlatformUtil: PlatformUtil.PlatformUtil = {
  platform: 'browser',
  is: (platform) => platform === 'browser',
  openLink: async (href, target = '_blank') => {
    return Result.try({
      try: () => window.open(href, target),
      catch: (cause) =>
        new PlatformError({
          platform: 'browser',
          method: 'openLink',
          cause,
        }),
    }).pipe(
      Result.flatMap((x) =>
        x === null
          ? Result.fail(
              new PlatformError({
                platform: 'browser',
                method: 'openLink',
                cause: new Error('Window is null'),
              }),
            )
          : Result.succeed(void x),
      ),
    )
  },
  getDeviceId: async () => {
    if (cachedDeviceId) {
      return cachedDeviceId
    }

    const deviceId = getFallbackDeviceIdFromLocalStorage()

    cachedDeviceId = deviceId
    return deviceId
  },
  setScaleFactor,
  share: (data) =>
    Result.tryPromise({
      try: () => navigator.share(data),
      catch: (cause) =>
        new PlatformError({
          platform: 'browser',
          method: 'share',
          cause,
        }),
    }),
  getVersion: async () => environment.packageVersion,
  impactFeedback: async (style: ImpactHapticFeedbackStyle) => {
    if (
      'vibrate' in window.navigator &&
      typeof window.navigator.vibrate === 'function'
    ) {
      window.navigator.vibrate(impactDuration[style])
    }
  },
  copyToClipboard,
  copyImageToClipboard: async (blob, mimeType) =>
    Result.tryPromise({
      try: () =>
        navigator.clipboard.write([new ClipboardItem({ [mimeType]: blob })]),
      catch: (cause) =>
        new PlatformError({
          platform: 'browser',
          method: 'share',
          cause,
        }),
    }),
  readFromClipboard: async () => {
    try {
      const text = await navigator.clipboard.readText()
      return Result.succeed(text)
    } catch (error) {
      return Result.fail(UnknownError.fromUnknown(error))
    }
  },
  locale: async () => undefined,
  hostPlatform: () => undefined,
  deeplink: {
    getCurrent: async () => [window.location.href],
    onOpenUrl: async () => () => {},
  },
  window: {
    minimize: async () => {},
    close: async () => {},
    toggleMaximize: async () => {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        document.documentElement.requestFullscreen()
      }
    },
  },
  downloadFileFromURL: async (url, fileName) => {
    DomUtil.downloadFile(url, fileName)
    return Result.succeed(void 0)
  },
  downloadFile: async (blob, fileName) => {
    const url = URL.createObjectURL(blob)
    DomUtil.downloadFile(url, fileName)
    URL.revokeObjectURL(url)
    return Result.succeed(void 0)
  },
  relaunch: async () =>
    Result.tryPromise({
      try: async () => location.reload(),
      catch: (cause) =>
        new PlatformError({
          platform: 'browser',
          method: 'relaunch',
          cause,
        }),
    }),

  check: (options) => {
    const { timeout } = options ?? {}

    return Result.tryPromise({
      try: async () => {
        const response = await fetch(environment.latestReleaseUrl, {
          signal: timeout ? AbortSignal.timeout(timeout) : undefined,
        })
        const json = await response.json()

        if (
          typeof json === 'object' &&
          json !== null &&
          'version' in json &&
          typeof json.version === 'string'
        ) {
          let notes: undefined | string = undefined
          if ('notes' in json && typeof json.notes === 'string') {
            notes = json.notes
          }
          const currentVersion = environment.packageVersion
          const version = json.version
          if (currentVersion === version) {
            return
          }

          return new BrowserUpdate({
            currentVersion: environment.packageVersion,
            version: json.version,
            notes,
          })
        }

        throw new Error('Error during update checking.')
      },
      catch: (cause) =>
        new PlatformError({
          platform: 'browser',
          method: 'check',
          cause,
        }),
    })
  },
}

class BrowserUpdate implements PlatformUtil.PlatformUpdate {
  version: string
  currentVersion: string
  notes?: string | undefined
  constructor(args: {
    version: string
    currentVersion: string
    notes?: string | undefined
  }) {
    this.version = args.version
    this.currentVersion = args.currentVersion
    this.notes = args.notes
  }

  async download(options: {
    onStarted: (payload: { contentLength?: number | undefined }) => void
    onProgress: (payload: { chunkLength: number }) => void
    onFinished: () => void
  }) {
    options.onStarted({})
    options.onProgress({ chunkLength: 1 })
    options.onFinished()
    return Result.succeed(undefined)
  }

  async install() {
    return Result.succeed(undefined)
  }
}
